import type {NotificationModel} from 'app/models/NotificationModel'
import {getFamilyNotificationLink} from 'app/pages/student/Notifications/utils/notificationLink'
import clsx from 'clsx'
import {hasPermission, isLyceum} from 'common/HelperFunctions'
import constants from 'common/constants'
import {getStudentFeePath} from 'common/helpers/fee'
import {
  buildNotificationLink,
  getNotificationsInboxPath,
  isInstituteUserType,
  openNotificationLink,
} from 'common/helpers/notifications'
import moment from 'moment'
import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, useLocation, useNavigate} from 'react-router'
import {NotificationReducer, RootState} from 'setup'
import {
  getNotifications,
  getNotificationsCount,
  readAllNotifications,
  readNotifications,
} from 'setup/redux/Actions/Notifications/notifications'

import {MenuComponent} from '_metronic/assets/ts/components'
import {KTSVG, toAbsoluteUrl} from '_metronic/helpers'

type NotificationRow = NotificationModel & {state?: string; icon?: string}

const NotificationItem: FC<{
  notification: NotificationRow
  isInstituteUser: boolean
  readNotification: (id: number | string) => void
  closeMenu: () => void
}> = ({notification, isInstituteUser, readNotification, closeMenu}) => {
  const navigate = useNavigate()
  // Families follow the mobile app routing by notification type.
  const notificationLink = isInstituteUser
    ? buildNotificationLink(notification.webUrl, true)
    : getFamilyNotificationLink(notification, {
        can: hasPermission,
        lyceum: isLyceum(),
        feePath: getStudentFeePath(),
      })

  const handleClick = () => {
    closeMenu()
    readNotification(notification.id)
    openNotificationLink(notificationLink, notification.studentUserId, navigate)
  }

  const cleanedDescription = notification.description
    ? notification.description.replace(/\\n/g, '\n')
    : ''

  return (
    <div
      className='d-flex flex-stack my-4 cursor-pointer'
      onClick={handleClick}
      data-testid='notification-item'
    >
      <div className='d-flex align-items-center'>
        <div className='symbol symbol-35px me-4'>
          <span className={clsx('symbol-label', `bg-light-${notification?.state ?? 'primary'}`)}>
            <KTSVG
              path={`/media/${notification?.icon ?? 'icons/duotune/general/gen044.svg'}`}
              className={`svg-icon-2 svg-icon-${notification?.state ?? 'primary'}`}
            />
          </span>
        </div>

        <div className='mb-0 me-2'>
          <Link to={notificationLink} className='fs-6 text-gray-800 text-hover-primary fw-bolder'>
            {notification.new && (
              <span
                className='bullet bullet-dot bg-success h-8px w-8px animation-blink me-1'
                data-testid='notification-unread-dot'
              />
            )}
            {notification.title}
          </Link>

          <div className='text-gray-400 fs-7'>
            {cleanedDescription
              .trim()
              .split('\n')
              .map((line, index, arr) => (
                <React.Fragment key={index}>
                  {line.trim()}
                  {index < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
          </div>
        </div>
      </div>

      <span className='badge badge-light fs-8'>{moment(notification.date).fromNow()}</span>
    </div>
  )
}

/**
 * Header bell dropdown. Opening it marks every unread notification as read
 * (the server-side "read all"), which is what clears the badge; the list is
 * refreshed once the menu closes so the unread dots disappear without the
 * items jumping while the user is looking at them.
 */
const HeaderNotificationsMenu: FC = () => {
  const dropDownMenuRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const [notifications, setNotifications] = useState<NotificationRow[]>([])
  const [notificationsCount, setNotificationsCount] = useState(0)
  const [newNotificationsCount, setNewNotificationsCount] = useState(0)

  const location = useLocation()
  const dispatch = useDispatch()

  const notificationsReducer = useSelector<RootState>(
    ({notificationsReducer}) => notificationsReducer,
    shallowEqual
  ) as NotificationReducer

  const userType = useSelector<RootState>(
    ({userReducer}) => userReducer.userType,
    shallowEqual
  ) as string

  const isInstituteUser = isInstituteUserType(userType)
  const viewAllURL = getNotificationsInboxPath(isInstituteUser)

  // The menu open/close observer is registered once, so it reads the latest
  // unread count and pending-refresh flag through refs instead of closures.
  const unreadCountRef = useRef(0)
  const refreshOnCloseRef = useRef(false)
  useEffect(() => {
    unreadCountRef.current = notificationsReducer.notificationsCount
  }, [notificationsReducer.notificationsCount])

  const loadNotifications = useCallback(() => {
    dispatch(
      getNotifications(
        {page: 1, pageSize: 100},
        (res: {result: {data: NotificationRow[]; totalCount: number}}) => {
          setNotifications(res.result.data)
          setNotificationsCount(res.result.totalCount)
          setNewNotificationsCount(res.result.data.filter((n) => n.new).length)
        }
      )
    )
  }, [dispatch])

  const loadNewNotificationsCount = useCallback(() => {
    dispatch(getNotificationsCount())
  }, [dispatch])

  const readNotification = (id: number | string) => {
    if (!newNotificationsCount) return
    dispatch(readNotifications({notificationId: [id]}, newNotificationsCount - 1))
  }

  const closeMenu = () => {
    if (!dropDownMenuRef.current) return

    const menu = MenuComponent.getInstance(dropDownMenuRef.current)
    const trigger = menu?.getTriggerElement()

    if (menu && trigger) {
      menu.hide(trigger)
    }
  }

  useEffect(() => {
    loadNotifications()
    loadNewNotificationsCount()
    const notificationsInterval = setInterval(() => {
      if (location.pathname !== '/dashboard') return
      loadNotifications()
    }, constants.notification_polling_delay)

    const notificationsCountInterval = setInterval(() => {
      if (location.pathname !== '/dashboard') return
      loadNewNotificationsCount()
    }, constants.notification_polling_delay)

    return () => {
      clearInterval(notificationsInterval)
      clearInterval(notificationsCountInterval)

      setNotifications([])
      setNotificationsCount(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const menu = dropDownMenuRef.current
    if (!menu) return

    // Metronic toggles the `show` class on the dropdown; that is the only
    // signal we get for "the user opened the bell".
    const observer = new MutationObserver(() => {
      const isOpen = menu.classList.contains('show')

      if (isOpen) {
        if (unreadCountRef.current > 0) {
          dispatch(
            readAllNotifications(() => {
              refreshOnCloseRef.current = true
              setNewNotificationsCount(0)
            })
          )
        }
        return
      }

      if (refreshOnCloseRef.current) {
        refreshOnCloseRef.current = false
        loadNotifications()
        loadNewNotificationsCount()
      }
    })

    observer.observe(menu, {attributes: true, attributeFilter: ['class']})

    return () => observer.disconnect()
  }, [dispatch, loadNotifications, loadNewNotificationsCount])

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
      data-kt-menu='true'
      ref={dropDownMenuRef}
      data-testid='notifications-menu'
    >
      <div
        className='d-flex flex-column bgi-no-repeat rounded-top'
        style={{backgroundImage: `url('${toAbsoluteUrl('/media/misc/pattern-1.jpg')}')`}}
      >
        <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
          {intl.formatMessage({id: 'NOTIFICATION.HEADING'})}
          <span className='fs-8 opacity-75 ms-3'>{notificationsCount}</span>
        </h3>
      </div>

      <div className='tab-content'>
        <div className='tab-pane fade show active' id='kt_topbar_notifications_1' role='tabpanel'>
          <div className='scroll-y mh-325px my-5 px-8'>
            {notifications.length < 1 && (
              <div>{intl.formatMessage({id: 'NOTIFICATION.NO_NEW_NOTIFICATION'})}</div>
            )}
            {notifications.map((notification) => (
              <NotificationItem
                notification={notification}
                isInstituteUser={isInstituteUser}
                readNotification={readNotification}
                closeMenu={closeMenu}
                key={notification.userNotificationId ?? notification.id}
              />
            ))}
          </div>

          {/* families only reach the inbox with notification_manager */}
          {(isInstituteUser || hasPermission('notification_manager')) && (
            <div className='py-3 text-center border-top'>
              <Link
                to={viewAllURL}
                className='btn btn-color-gray-600 btn-active-color-primary'
                onClick={closeMenu}
              >
                {intl.formatMessage({id: 'GENERAL.VIEW_ALL'})}
                <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-5' />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {HeaderNotificationsMenu}
